import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminBar from '../components/AdminBar';
import { useThemeMode } from '../components/ThemeModeContext';
import { getStoredProfileState, PROFILE_STATE_UPDATED_EVENT } from '../components/profileIconUtils';
import { serverDomain } from './config';

const getFriendlyError = (status) => {
  switch (status) {
    case 401:
      return 'Your session has expired. Please log in again.';
    case 422:
      return 'Missing or invalid app information. Please try again.';
    case 404:
      return 'We could not find your account.';
    case 500:
      return 'Something went wrong on our side. Please try again later.';
    default:
      return 'Unable to load your account at this time.';
  }
};

export default function AccountPage() {
  const { isDarkMode } = useThemeMode();
  const headerBg = isDarkMode ? '#000000' : '#003366';
  const headerText = '#ffffff';
  const pageBg = isDarkMode ? '#1a1a1a' : '#ffffff';
  const pageText = isDarkMode ? '#f5f5f5' : '#000000';

  const [loading, setLoading] = useState(true);
  const [credit, setCredit] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [missingCredentials, setMissingCredentials] = useState(false);
  const [profile, setProfile] = useState({
    username: localStorage.getItem('appstar_username') || '',
    firstName: localStorage.getItem('appstar_first_name') || '',
    lastName: localStorage.getItem('appstar_last_name') || '',
    email: localStorage.getItem('appstar_email') || ''
  });

  const syncProfileFromStorage = React.useCallback(() => {
    const stored = getStoredProfileState();
    setProfile({
      username: stored.username || '',
      firstName: stored.firstName || '',
      lastName: stored.lastName || '',
      email: stored.email || ''
    });
  }, []);

  const parseCreditValue = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const formatCredit = (value) => {
    const creditValue = parseCreditValue(value);
    if (creditValue === null) return 'Account error. Please contact support.';
    return Number.isInteger(creditValue) ? String(creditValue) : creditValue.toFixed(2);
  };

  const formatAmountFromCredit = (value) => {
    const creditValue = parseCreditValue(value);
    if (creditValue === null) return 'Account error. Please contact support.';
    const amount = creditValue / 2;
    return `$${amount.toFixed(2)}`;
  };

  useEffect(() => {
    const userId = localStorage.getItem('appstar_user_id');
    const token = localStorage.getItem('appstar_jwt');

    syncProfileFromStorage();

    if (!userId || !token) {
      setMissingCredentials(true);
      setLoading(false);
      return;
    }

    const url = serverDomain + 'get-user-remaining-credit.php?user_id=' + userId;

    setLoading(true);
    axios
      .get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      .then((response) => {
        //log response for debugging
        console.log('get-user-remaining-credit response:', response);
        const data = response?.data || {};
        if (data.success === 1) {
          setCredit(data.credit);
          setErrorMessage('');
        } else {
          const status = Number(data.status || response?.status);
          console.error('get-user-remaining-credit failed:', {
            status,
            message: data.message,
            data
          });
          setErrorMessage(getFriendlyError(status));
        }
      })
      .catch((error) => {
        const status = Number(error?.response?.status);
        const data = error?.response?.data;
        console.error('get-user-remaining-credit error:', {
          status,
          message: data?.message || error?.message,
          data
        });
        setErrorMessage(getFriendlyError(status));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [syncProfileFromStorage]);

  useEffect(() => {
    const handleProfileStateUpdated = () => {
      syncProfileFromStorage();
    };

    window.addEventListener('storage', handleProfileStateUpdated);
    window.addEventListener(PROFILE_STATE_UPDATED_EVENT, handleProfileStateUpdated);
    return () => {
      window.removeEventListener('storage', handleProfileStateUpdated);
      window.removeEventListener(PROFILE_STATE_UPDATED_EVENT, handleProfileStateUpdated);
    };
  }, [syncProfileFromStorage]);

  const profileRows = [
    { label: 'First name', value: profile.firstName },
    { label: 'Last name', value: profile.lastName },
    { label: 'Email', value: profile.email },
    { label: 'Username', value: profile.username }
  ].filter((item) => item.value);

  return (
    <div style={{ minHeight: '100vh', background: pageBg, color: pageText }}>
      <AdminBar bgColor={headerBg} textColor={headerText} showProfileIcon />
      <div style={{ padding: '60px 24px 24px', maxWidth: 900, margin: '0 auto' }}>
        <h1 style={{ margin: 0, fontSize: '1.8rem' }}>Account</h1>
        {!missingCredentials && profileRows.length > 0 && (
          <div style={{ marginTop: 10, marginBottom: 8, maxWidth: 420 }}>
            {profileRows.map((item) => (
              <div key={item.label} style={{ marginTop: 8 }}>
                <div style={{ fontSize: '0.86rem', fontWeight: 600, letterSpacing: '0.02em', opacity: 0.8, textTransform: 'uppercase' }}>
                  {item.label}
                </div>
                <div style={{ marginTop: 2, fontSize: '1rem', fontWeight: 500 }}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        )}
        {loading && (
          <p style={{ marginTop: 16 }}>Loading your remaining credit...</p>
        )}
        {!loading && missingCredentials && (
          <p style={{ marginTop: 16 }}>
            Missing account credentials. Please log in again.
          </p>
        )}
        {!loading && !missingCredentials && errorMessage && (
          <p style={{ marginTop: 16 }}>{errorMessage}</p>
        )}
        {!loading && !missingCredentials && !errorMessage && (
          <div style={{ marginTop: 20, maxWidth: 420, padding: 20, borderRadius: 14, background: isDarkMode ? '#222' : '#f7f7f7' }}>
            <div style={{ fontSize: '2rem', fontWeight: 700, marginBottom: 14 }}>Pay as you go</div>
            <div style={{ fontSize: '1rem', opacity: 0.85 }}>Credit</div>
            <div style={{ marginTop: 8, fontSize: '3.1rem', fontWeight: 500, lineHeight: 1.1 }}>
              {formatCredit(credit)}
            </div>
            <div style={{ marginTop: 14, fontSize: '1rem', opacity: 0.85 }}>Amount</div>
            <div style={{ marginTop: 6, fontSize: '1.5rem', fontWeight: 500, lineHeight: 1.2 }}>
              {formatAmountFromCredit(credit)}
            </div>
            <button
              type="button"
              style={{ marginTop: 18, padding: '12px 20px', borderRadius: 12, border: 'none', fontSize: '1.15rem', fontWeight: 600, cursor: 'pointer', background: isDarkMode ? '#3a3a3a' : '#e6e6e6', color: isDarkMode ? '#f1f1f1' : '#222' }}
            >
              Add to credit balance
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
